/**
 * Le funzioni e le costanti di `schemaCrud.tsx`.
 *
 * Stavano nel file del componente, e `react-refresh/only-export-components` lo segnalava:
 * un file che esporta un componente **e** altro rende l'aggiornamento a caldo meno
 * granulare — a ogni modifica qui si rimonta il componente invece di scambiarlo.
 */
import type { ReactNode } from 'react';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import type { FilterClause } from '@/shared/api/jsonapi/types';

/** `icon`: un nome Font Awesome scelto da un selettore visivo — vedi `shared/components/IconPicker`. */
/**
 * `monthDay` (giorno e mese, sul filo `MM-DD`), `time` (ore e minuti, sul filo `HH:mm:00`), `weekday`
 * (sul filo 1–7), `dayList` (giorni a pastiglie, sul filo `"3,1"`): vedi `specialFields.tsx`.
 */
export type CrudFieldKind = 'text' | 'number' | 'decimal' | 'date' | 'select' | 'switch' | 'textarea' | 'employee' | 'reference' | 'referenceMany' | 'threshold' | 'csvMulti' | 'icon'
  | 'monthDay' | 'time' | 'weekday' | 'dayList';
export interface CrudField {
  name: string;
  label: string;
  kind: CrudFieldKind;
  options?: readonly string[];
  optionLabels?: Record<string, string>;
  required?: boolean;
  requiredWhen?: (v: Record<string, unknown>) => boolean;
  /**
   * Il campo si mostra solo quando questa dice sì. Nascosto, **si manda vuoto** e non conta fra gli
   * obbligatori: un valore rimasto nello stato di un campo che non si vede è un 422 su una cosa che
   * chi compila non può vedere né correggere.
   */
  visibleWhen?: (v: Record<string, unknown>) => boolean;
  /** Per kind 'reference': type plurale della risorsa collegata (kind 'employee' = erp-employees). */
  refType?: string;
  /**
   * Per kind 'reference': disabilita una singola opzione, restituendo il motivo (che finisce
   * nell'etichetta) oppure `null` per lasciarla scegliibile.
   *
   * Serve dove una scelta è formalmente valida ma il server la rifiuterebbe al salvataggio:
   * mostrarla disabilitata col perché è meglio sia che nasconderla (chi l'ha configurata la
   * cercherebbe) sia che offrirla (si scoprirebbe l'errore a form compilato).
   */
  refDisabledWhen?: (row: Denormalized) => string | null;
  /** Per kind 'reference': filtra le opzioni dai valori del form; `null` = non ancora sceglibile. */
  refFilter?: (v: FormValues) => Record<string, FilterClause> | null;
  /** Per kind 'reference': il campo da cui dipende. Cambiandolo, questo si svuota. */
  dependsOn?: string;
  /** Per kind 'reference': cerca sul server sui campi indicati, invece delle prime 100 righe. */
  refSearch?: { fields: readonly string[]; sort?: string[] };
  /**
   * Per kind 'threshold': nome dell'attributo soglia (int nullable) abbinato al flag in `name`.
   * Il BE fa prevalere il flag sulla soglia, quindi i due campi separati permettono uno stato
   * ambiguo (flag ON + soglia valorizzata, con la soglia ignorata). Il controllo a tre stati
   * lo rende non producibile.
   */
  thresholdField?: string;
  /** Etichetta dell'opzione "oltre N" (default: "oltre N giorni"). */
  thresholdLabel?: string;
  /** Testo di aiuto sotto il controllo. */
  hint?: string;
  /**
   * Quel che del campo cambia coi valori degli altri: il controllo, l'etichetta, la spiegazione.
   * Per esempio «Quale giorno» è un elenco di giorni della settimana con «ogni venerdì», un numero
   * con «il 5 del mese».
   */
  variant?: (v: Record<string, unknown>) => Partial<Pick<CrudField, 'kind' | 'label' | 'hint' | 'options' | 'optionLabels'>>;
  /**
   * Il nome del campo da cui il valore si propone, alla creazione: il codice dal nome. Segue la
   * sorgente finché nessuno lo cambia a mano.
   */
  suggestFrom?: string;
  /** Testo mostrato quando il campo e' vuoto (es. "Eredita dal tenant" su una select nullable). */
  placeholder?: string;
  /** Valore iniziale in **creazione** (in modifica vince sempre il dato della riga).
   *
   * Serve soprattutto agli `switch`: senza, ogni flag parte da `false`, quindi una
   * risorsa creata da qui nasceva con `isActive` spento — cioè inutilizzabile, e senza
   * un posto dove riaccenderla se la pagina non offriva la modifica.
   */
  defaultValue?: string | number | boolean;
  /** Se true, l'attributo non viene inviato in fase di creazione (POST) perché il BE lo vieta. */
  readOnlyOnCreate?: boolean;
  /**
   * Si scrive alla creazione e poi resta fermo: in modifica il campo è spento e al posto della
   * spiegazione si legge questo testo, che dice **perché** (regola 7: niente spento senza motivo).
   */
  lockedOnEdit?: string;
  /** A tutta larghezza anche se non è un'area di testo: per i campi con una spiegazione lunga. */
  fullWidth?: boolean;
}
export interface CrudColumn {
  key: string;
  label: string;
  render?: (row: Denormalized) => string;
  /**
   * La cella disegnata, solo nella tabella di `SchemaResourcePanel`: badge e icone dove una
   * stringa non basta. `render` resta il testo, che serve a chi esporta.
   */
  cell?: (row: Denormalized) => ReactNode;
}
export const isVisible = (f: CrudField, v: Record<string, unknown>): boolean => !f.visibleWhen || f.visibleWhen(v);
/** Il campo com'è con questi valori: `variant` applicata. */
export const resolveField = (f: CrudField, v: Record<string, unknown>): CrudField => (f.variant ? { ...f, ...f.variant(v) } : f);
export const isRef = (f: CrudField): boolean => f.kind === 'employee' || f.kind === 'reference';
export function labelOf(row: Denormalized): string {
  const r = row as Record<string, unknown>;
  const person = [r.lastName, r.firstName].filter(Boolean).join(' ');
  return (r.name as string) || (r.code as string) || person || (r.email as string) || (r.title as string) || (r.description as string) || String(r.id);
}
// ─────────── column helpers ───────────
export const enumCol = (key: string, label: string, labels: Record<string, string>): CrudColumn =>
  ({ key, label, render: (r) => { const v = (r as Record<string, unknown>)[key] as string | undefined; return v ? (labels[v] ?? v) : '—'; } });
export const dateCol = (key: string, label: string): CrudColumn =>
  ({ key, label, render: (r) => { const v = (r as Record<string, unknown>)[key] as string | undefined; return v ? v.slice(0, 10).split('-').reverse().join('/') : '—'; } });
export const boolCol = (key: string, label: string): CrudColumn =>
  ({ key, label, render: (r) => ((r as Record<string, unknown>)[key] ? 'Sì' : 'No') });
export const refCol = (key: string, label: string): CrudColumn =>
  ({ key, label, render: (r) => { const p = (r as Record<string, unknown>)[key] as Denormalized | null | undefined; return p ? labelOf(p) : '—'; } });
export type FormValues = Record<string, unknown>;
/** Valori iniziali del form di creazione. `defaultValue` del campo, se dichiarato, vince. */
export function emptyValues(fields: CrudField[]): FormValues {
  const v: FormValues = {};
  for (const f of fields) {
    if (f.kind === 'threshold') { v[f.name] = false; if (f.thresholdField) v[f.thresholdField] = ''; continue; }
    if (f.defaultValue !== undefined) { v[f.name] = f.defaultValue; continue; }
    v[f.name] = f.kind === 'switch' ? false : f.kind === 'referenceMany' ? [] : (f.kind === 'date' || isRef(f)) ? null : '';
  }
  return v;
}
